import { useCallback, useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

export function Lightbox({ photos, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [onClose, onPrev, onNext]);

  const photo = photos[index];
  return (
    <motion.div
      data-testid="gallery-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        data-testid="gallery-lightbox-close"
        onClick={onClose}
        aria-label="Chiudi"
        className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center border border-neutral-800 text-neutral-300 transition-colors duration-200 hover:border-red-600 hover:text-red-500"
      >
        <X className="h-5 w-5" />
      </button>
      <button
        data-testid="gallery-lightbox-prev"
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        aria-label="Precedente"
        className="absolute left-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center border border-neutral-800 bg-black/60 text-neutral-300 transition-colors duration-200 hover:border-red-600 hover:text-red-500 sm:left-8"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        data-testid="gallery-lightbox-next"
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        aria-label="Successiva"
        className="absolute right-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center border border-neutral-800 bg-black/60 text-neutral-300 transition-colors duration-200 hover:border-red-600 hover:text-red-500 sm:right-8"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      <motion.figure
        key={photo.src}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="flex max-h-full flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          data-testid="gallery-lightbox-image"
          src={photo.src}
          alt={photo.alt}
          className="max-h-[80vh] max-w-full border border-neutral-800 object-contain"
        />
        <figcaption className="mt-4 flex items-center gap-4 font-mono-tac text-xs uppercase tracking-[0.3em] text-neutral-500">
          <span className="text-red-600">
            {String(index + 1).padStart(2, "0")} / {String(photos.length).padStart(2, "0")}
          </span>
          <span>{photo.alt}</span>
        </figcaption>
      </motion.figure>
    </motion.div>
  );
}

export const resolveSrc = (src) => (src.startsWith("/api/") ? `${API}${src}` : src);

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/gallery`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setPhotos(list.map((p) => ({ ...p, src: resolveSrc(p.src) }))))
      .catch(() => setPhotos([]));
  }, []);
  const n = photos.length;
  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(
    () => setActive((i) => (i === null ? i : (i - 1 + n) % n)),
    [n]
  );
  const next = useCallback(
    () => setActive((i) => (i === null ? i : (i + 1) % n)),
    [n]
  );

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative border-t border-neutral-900"
    >
      <div className="mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
        <motion.p
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="font-mono-tac text-xs uppercase tracking-[0.35em] text-red-600"
        >
          05 — Galleria
        </motion.p>
        <motion.h2
          variants={reveal}
          custom={1}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="font-display mt-4 text-5xl uppercase leading-none text-neutral-100 sm:text-7xl"
        >
          Sul palco e sul <span className="text-[#C8102E]">mare</span>
        </motion.h2>
        <motion.p
          variants={reveal}
          custom={2}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="font-lyric mt-5 max-w-xl text-xl italic text-neutral-400"
        >
          Luci rosse, quattro amplificatori e il mare come platea.
        </motion.p>

        <div className="mt-14 columns-2 gap-3 md:columns-3">
          {photos.map((photo, i) => (
            <motion.button
              key={photo.id}
              variants={reveal}
              custom={i % 3}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
              data-testid={`gallery-photo-${i + 1}`}
              onClick={() => setActive(i)}
              aria-label={`Apri foto: ${photo.alt}`}
              className={`group relative mb-3 block w-full break-inside-avoid overflow-hidden border border-neutral-800 text-left ${
                photo.wide ? "aspect-[16/10]" : "aspect-[3/4]"
              }`}
            >
              <img
                src={photo.src}
                alt={photo.alt}
                loading="lazy"
                className="h-full w-full object-cover grayscale-[0.5] transition-all duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-4 opacity-0 transition-all duration-300 group-hover:opacity-100">
                <span className="font-mono-tac text-[10px] uppercase tracking-[0.25em] text-neutral-200">
                  {photo.alt}
                </span>
                <Maximize2 className="h-4 w-4 shrink-0 text-red-500" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <Lightbox
            photos={photos}
            index={active}
            onClose={close}
            onPrev={prev}
            onNext={next}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
